import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2, ShieldCheck } from "lucide-react";
import { STRIPE_PACKAGES } from "../../data/stripePackages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Server-priced Stripe checkout. Renders package options and, on click, asks
 * the backend to create a Checkout Session (amount fixed server-side) then
 * redirects to Stripe. `group` filters which packages show.
 */
export const CheckoutPackages = ({
  group = "all",
  submissionId = null,
  submissionKind = null,
  title = "Ready to start? Pay securely to lock it in.",
}) => {
  const [loadingKey, setLoadingKey] = useState(null);
  const [error, setError] = useState(null);

  const packages = STRIPE_PACKAGES.filter(
    (p) => group === "all" || p.group === group,
  );

  const startCheckout = async (lookupKey) => {
    if (loadingKey) return;
    setLoadingKey(lookupKey);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        submission_id: submissionId,
        submission_kind: submissionKind,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Could not start checkout. Please try again.");
        setLoadingKey(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not start checkout. Please try again.",
      );
      setLoadingKey(null);
    }
  };

  return (
    <div data-testid="checkout-packages" className="mt-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay-deep whitespace-nowrap">
          Secure Checkout
        </span>
        <span className="h-px flex-1 bg-sand-300" />
      </div>
      <p className="font-display text-lg sm:text-xl text-ink mb-4">{title}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {packages.map((p) => (
          <div
            key={p.key}
            data-testid={`checkout-card-${p.key}`}
            className="flex flex-col border border-sand-300 bg-sand-50 p-5 transition-colors hover:border-clay"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-display text-[17px] font-semibold text-ink leading-snug">
                {p.name}
              </span>
              <span className="font-mono text-[13px] text-clay-deep tabular-nums whitespace-nowrap">
                {p.price}
                {p.period && <span className="text-ink-muted">{p.period}</span>}
              </span>
            </div>
            <p className="mt-1.5 text-[13px] text-ink-muted leading-relaxed">
              {p.blurb}
            </p>
            {p.features && (
              <ul className="mt-3 space-y-1.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[12.5px] text-ink leading-snug">
                    <Check size={13} className="mt-0.5 shrink-0 text-olive" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => startCheckout(p.key)}
              disabled={!!loadingKey}
              data-testid={`checkout-pay-${p.key}`}
              className="mt-4 group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-3 text-[13.5px] font-medium hover:bg-ink transition-colors min-h-[46px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingKey === p.key ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Redirecting…
                </>
              ) : p.recurring ? (
                <>
                  Subscribe {p.price}
                  {p.period}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </>
              ) : (
                <>
                  Pay {p.price}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {error && (
        <p data-testid="checkout-error" className="mt-4 text-[13px] text-rust">
          {error}
        </p>
      )}
      <p className="mt-4 flex items-center gap-2 text-[12px] text-ink-muted">
        <ShieldCheck size={13} className="text-olive" />
        Payments processed securely by Stripe. Test mode.
      </p>
    </div>
  );
};

export default CheckoutPackages;
